import { useRef, useState, useEffect } from "react";
import { Play, ArrowRight, Volume2 } from "lucide-react";

const VIDEO_SRC = "/apresentacao-v2.mp4"; // PLACEHOLDER — Junior vai subir o arquivo em public/
const PERCENTUAL_LIBERAR = 0.99;

export default function ApresentacaoVideoGate() {
  const videoRef = useRef(null);
  const infoRef = useRef(null);
  const [liberado, setLiberado] = useState(false);
  const [maxVisto, setMaxVisto] = useState(0); // segundo máximo realmente alcançado
  const [maisInfoAberto, setMaisInfoAberto] = useState(false);
  const [videoErro, setVideoErro] = useState(false);
  const [comSom, setComSom] = useState(false);
  const [seekBloqueado, setSeekBloqueado] = useState(false);

  // Rastreia tempo real assistido (não aceita seek pra frente — se pular, maxVisto não avança)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate = () => {
      // Só avança maxVisto se o tempo atual é "próximo" do maxVisto anterior
      // (margem de 1.5s pra aceitar reprodução natural, mas rejeitar saltos grandes)
      if (video.currentTime <= maxVisto + 1.5) {
        setMaxVisto((prev) => Math.max(prev, video.currentTime));
      } else {
        // Pulou — volta pro ponto máximo visto
        video.currentTime = maxVisto;
      }
    };

    const onEnded = () => {
      // Checa se a cobertura real é suficiente
      if (video.duration > 0 && maxVisto / video.duration >= PERCENTUAL_LIBERAR) {
        setLiberado(true);
      }
    };

    const onLoadedMetadata = () => {
      // Em cada tick, verifica se já alcançou o percentual
      // (libera antes de o vídeo acabar se a pessoa assistiu 95%+)
    };

    const onSeeking = () => {
      const tolerancia = 0.5; // segundos de margem para seek "natural"
      if (video.currentTime > maxVisto + tolerancia) {
        video.currentTime = maxVisto;
        setSeekBloqueado(true);
        setTimeout(() => setSeekBloqueado(false), 2000);
      }
    };

    const onError = () => {
      setVideoErro(true);
    };

    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("ended", onEnded);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("seeking", onSeeking);
    video.addEventListener("error", onError);

    return () => {
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("seeking", onSeeking);
      video.removeEventListener("error", onError);
    };
  }, [maxVisto]);

  // Libera o botão quando o percentual é atingido (não precisa esperar ended)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    if (maxVisto / video.duration >= PERCENTUAL_LIBERAR) {
      setLiberado(true);
    }
  }, [maxVisto]);

  const ativarSom = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    setComSom(true);
    video.play().catch(() => {}); // Alguns browsers pausam ao desmutar sem interação
  };

  const handleMaisInfo = () => {
    setMaisInfoAberto(true);
    setTimeout(() => {
      infoRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6">
      {/* Cabeçalho */}
      <div className="text-center mb-4 md:mb-10">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/15 px-3 py-1">
          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-green-400">
            Apresentação
          </span>
        </div>
        <h1 className="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white tracking-tight leading-[1.05] mb-3 md:mb-4">
          Conheça o{" "}
          <span className="bg-gradient-to-r from-green-400 to-emerald-300 bg-clip-text text-transparent">
            Evolzap
          </span>
        </h1>
        <p className="text-sm md:text-base text-zinc-400 leading-relaxed max-w-2xl mx-auto">
          Assista ao vídeo abaixo e descubra como o Evolzap pode transformar o atendimento e as vendas do seu negócio.
        </p>
      </div>

      {/* Vídeo com overlay inicial */}
      <div className="relative rounded-2xl overflow-hidden bg-[#111111] border border-white/10 shadow-2xl shadow-green-500/5 aspect-video mb-6">
        <video
          ref={videoRef}
          src={VIDEO_SRC}
          className="w-full h-full"
          autoPlay
          muted
          playsInline
          preload="auto"
          controls
          controlsList="nodownload noplaybackrate noremoteplayback"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
          onError={() => setVideoErro(true)}
        />
        {!comSom && !videoErro && (
          <button
            type="button"
            onClick={ativarSom}
            className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-4 bg-black/55 backdrop-blur-[2px] cursor-pointer group z-10 transition-all hover:bg-black/65"
            aria-label="Clique para ouvir o vídeo"
          >
            <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-green-500 group-hover:bg-green-400 flex items-center justify-center shadow-xl shadow-green-500/40 transition-all animate-pulse group-hover:animate-none group-hover:scale-110">
              <Volume2 className="w-10 h-10 md:w-14 md:h-14 text-black" strokeWidth={2.5} />
            </div>
            <span className="text-white font-bold text-lg md:text-2xl tracking-tight drop-shadow-lg">
              Clique para ouvir o vídeo
            </span>
            <span className="text-zinc-300 text-xs md:text-sm tracking-wide drop-shadow">
              O vídeo está tocando sem som
            </span>
          </button>
        )}
        {seekBloqueado && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-black/85 text-white px-4 py-2 rounded-lg text-sm font-medium border border-white/20 shadow-lg animate-fade-in pointer-events-none">
            Assista o vídeo completo para avançar
          </div>
        )}
        {videoErro && (
          <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-3 bg-black/80 px-6 text-center">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
              <Play className="w-10 h-10 md:w-12 md:h-12 text-zinc-600 ml-1" />
            </div>
            <span className="text-white font-semibold text-lg md:text-xl">
              Vídeo em breve
            </span>
            <span className="text-zinc-400 text-sm md:text-base max-w-md">
              O vídeo de apresentação será disponibilizado em breve. Volte em instantes.
            </span>
          </div>
        )}
      </div>

      {/* Progresso + CTA */}
      <div className="text-center">
        {!liberado && (
          <p className="text-sm text-zinc-500 mb-4">
            Continue assistindo para desbloquear mais informações
          </p>
        )}
        <button
          type="button"
          onClick={handleMaisInfo}
          disabled={!liberado}
          className={
            liberado
              ? "inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-black font-semibold px-6 py-3.5 rounded-xl shadow-lg shadow-green-500/20 hover:scale-[1.02] transition-all"
              : "inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-zinc-500 font-medium px-6 py-3.5 rounded-xl cursor-not-allowed"
          }
        >
          Clique aqui para mais informações
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* Seção "Mais Informações" — hidden até liberar */}
      {maisInfoAberto && (
        <div ref={infoRef} className="mt-20 md:mt-28 pt-10 border-t border-white/10 animate-fade-in">
          <div className="text-center mb-12">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/15 px-3 py-1">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-green-400">
                Sobre o Evolzap
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white tracking-tight leading-[1.05] mb-3 md:mb-4">
              Pra quem é o Evolzap
            </h2>
            <p className="text-sm md:text-base text-zinc-400 leading-relaxed max-w-2xl mx-auto">
              O Evolzap é para pequenas e médias empresas que querem organizar o atendimento no WhatsApp, Instagram Direct, Telegram e chat no site, automatizar vendas com Inteligência Artificial e ter CRM, campanhas, prospecção e financeiro em um só lugar.
            </p>
          </div>

          {/* Atalhos de download do app mobile */}
          <div className="max-w-md mx-auto mb-8">
            <p className="text-center text-xs md:text-sm text-zinc-500 uppercase tracking-wider font-semibold mb-3">
              Baixe o aplicativo
            </p>
            <div className="grid grid-cols-2 gap-3">
              {/* Card Google Play — destacado */}
              <a
                href="https://play.google.com/store/apps/details?id=com.evolzap.mobile&hl=pt_BR"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Baixar Evolzap na Google Play Store"
                className="flex flex-col items-center justify-center gap-2 p-4 md:p-5 bg-[#141414] border border-green-500/30 rounded-xl cursor-pointer hover:border-green-500/60 hover:bg-[#1a1a1a] hover:scale-[1.03] shadow-lg shadow-green-500/10 hover:shadow-green-500/20 transition-all group"
              >
                <svg viewBox="0 0 24 24" className="w-10 h-10 md:w-12 md:h-12 group-hover:scale-110 transition-transform">
                  <path fill="#FFC107" d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594z" />
                  <path fill="#FF3D00" d="M1.337.924a1.486 1.486 0 0 0-.337.963v20.225c0 .371.13.708.34.972l11.146-11.092L1.337.924z" />
                  <path fill="#4CAF50" d="M14.584 12.023l-3.543 3.521L1.34 22.084c.204.257.504.428.851.476.396.054.8-.029 1.137-.235l13.183-7.464-1.927-2.838z" />
                  <path fill="#1976D2" d="M14.584 12.023l1.927-2.838L3.328 1.721A1.482 1.482 0 0 0 2.19 1.486a1.49 1.49 0 0 0-.853.475l9.704 9.643 3.543-3.521z" />
                </svg>
                <div className="text-center">
                  <div className="text-zinc-400 text-[10px] md:text-xs leading-none mb-0.5 font-medium">Disponível no</div>
                  <div className="text-white font-bold text-sm md:text-base">Google Play</div>
                </div>
              </a>
              {/* Card App Store — inerte, "Em breve" */}
              <div className="relative flex flex-col items-center justify-center gap-2 p-4 md:p-5 bg-[#141414] border border-white/10 rounded-xl cursor-default opacity-60">
                <span className="absolute top-2 right-2 text-[9px] md:text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">
                  Em breve
                </span>
                <svg viewBox="0 0 24 24" className="w-10 h-10 md:w-12 md:h-12" fill="#FFFFFF">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <div className="text-center">
                  <div className="text-zinc-500 text-[10px] md:text-xs leading-none mb-0.5 font-medium">Baixe na</div>
                  <div className="text-white font-bold text-sm md:text-base">App Store</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA principal: acesso pelo navegador */}
          <div className="max-w-md mx-auto">
            <a
              href="https://app.evolzap.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-gradient-to-br from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 rounded-xl px-4 py-3 md:px-5 md:py-3.5 shadow-md shadow-green-500/15 hover:scale-[1.01] transition-all"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base md:text-lg font-bold text-black tracking-tight leading-tight">
                    Acesse agora pelo navegador
                  </h3>
                  <p className="text-xs md:text-sm text-black/75 font-medium leading-tight mt-0.5">
                    Comece grátis
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-black group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </div>
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
